/**
 * Predictive Search für Primedeco
 * Mit Kategorien und Produkte getrennt
 */

class PredictiveSearch {
  constructor() {
    this.searchInput = document.querySelector('.predictive-search__input');
    this.searchResults = document.querySelector('.predictive-search__results');
    this.searchForm = document.querySelector('.predictive-search__form');
    this.debounceTimer = null;
    this.currentQuery = '';
    this.minQueryLength = 2;
    
    this.init();
  }

  init() {
    if (!this.searchInput) return;
    
    this.searchInput.addEventListener('input', (e) => this.handleInput(e));
    this.searchInput.addEventListener('focus', () => {
      if (this.searchInput.value.trim().length >= this.minQueryLength) {
        this.showResults();
      }
    });
    
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.predictive-search')) {
        this.hideResults();
      }
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideResults();
        this.searchInput.blur();
      }
    });
  }

  handleInput(e) {
    const query = e.target.value.trim();
    
    clearTimeout(this.debounceTimer);
    
    if (query.length < this.minQueryLength) {
      this.hideResults();
      this.currentQuery = '';
      return;
    }
    
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query);
    }, 300);
  }

  async fetchResults(query) {
    if (query === this.currentQuery) return;
    this.currentQuery = query;
    
    try {
      // Hole Produkte UND Collections
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=10&resources[options][fields]=title,product_type,variants.title,vendor,tag,body`
      );
      
      if (!response.ok) throw new Error('Suchanfrage fehlgeschlagen');
      
      const data = await response.json();
      this.displayResults(data, query);
    } catch (error) {
      console.error('Fehler bei der Suche:', error);
      this.showError();
    }
  }

  displayResults(data, query) {
    if (!data.resources || !data.resources.results) {
      this.hideResults();
      return;
    }

    const products = data.resources.results.products || [];
    const collections = data.resources.results.collections || [];
    
    if (products.length === 0 && collections.length === 0) {
      this.showNoResults(query);
      return;
    }

    let resultsHTML = '';
    
    // === KATEGORIEN BEREICH ===
    if (collections.length > 0) {
      resultsHTML += `
        <div class="predictive-search__section">
          <div class="predictive-search__section-title">KATEGORIEN</div>
          <div class="predictive-search__collections-list">
      `;
      
      // Max 3 Kategorien anzeigen
      collections.slice(0, 3).forEach(collection => {
        resultsHTML += `
          <a href="${collection.url}" class="predictive-search__collection-item">
            <span class="predictive-search__collection-title">${this.highlightQuery(collection.title, query)}</span>
          </a>
        `;
      });
      
      resultsHTML += `
          </div>
        </div>
      `;
    }
    
    // === PRODUKTE BEREICH ===
    if (products.length > 0) {
      resultsHTML += `
        <div class="predictive-search__section">
          <div class="predictive-search__section-title">PRODUKTE</div>
          <div class="predictive-search__products-list">
      `;
      
      // Max 6 Produkte anzeigen
      products.slice(0, 6).forEach(product => {
        const imageUrl = product.featured_image?.url ? 
          this.getOptimizedImageUrl(product.featured_image.url, '100x100') : 
          this.getPlaceholderImage();
        
        const price = this.formatPrice(product.price);
        const compareAtPrice = product.compare_at_price_max > product.price ? 
          this.formatPrice(product.compare_at_price_max) : null;
        
        resultsHTML += `
          <a href="${product.url}" class="predictive-search__item">
            <div class="predictive-search__item-image">
              <img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy">
            </div>
            <div class="predictive-search__item-content">
              <h3 class="predictive-search__item-title">${this.highlightQuery(product.title, query)}</h3>
              <div class="predictive-search__item-price">
                ${compareAtPrice ? `<span class="predictive-search__price-compare">${compareAtPrice}</span>` : ''}
                <span class="predictive-search__price${compareAtPrice ? ' predictive-search__price--sale' : ''}">${price} *</span>
              </div>
            </div>
          </a>
        `;
      });
      
      resultsHTML += `
          </div>
        </div>
      `;
    }
    
    // === FOOTER ===
    resultsHTML += `
      <div class="predictive-search__footer">
        <a href="/search?q=${encodeURIComponent(query)}&type=product" class="predictive-search__view-all">
          Alle Ergebnisse für "${this.escapeHtml(query)}" anzeigen
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>
    `;
    
    this.searchResults.innerHTML = resultsHTML;
    this.showResults();
  }

  showNoResults(query) {
    this.searchResults.innerHTML = `
      <div class="predictive-search__no-results">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/>
          <path d="M21 21l-4.35-4.35"/>
        </svg>
        <p>Keine Ergebnisse für <strong>"${this.escapeHtml(query)}"</strong></p>
        <p class="predictive-search__no-results-hint">Versuchen Sie andere Suchbegriffe</p>
      </div>
    `;
    this.showResults();
  }

  showError() {
    this.searchResults.innerHTML = `
      <div class="predictive-search__error">
        <p>Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.</p>
      </div>
    `;
    this.showResults();
  }

  showResults() {
    this.searchResults.classList.add('predictive-search__results--visible');
  }

  hideResults() {
    this.searchResults.classList.remove('predictive-search__results--visible');
  }

  highlightQuery(text, query) {
    if (!query) return this.escapeHtml(text);
    
    const escapedText = this.escapeHtml(text);
    const escapedQuery = this.escapeHtml(query);
    const regex = new RegExp(`(${escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    
    return escapedText.replace(regex, '<mark>$1</mark>');
  }

  formatPrice(price) {
    let priceNum = parseFloat(price);
    
    // Shopify gibt Preis in Cents zurück wenn > 100
    if (priceNum > 100) {
      priceNum = priceNum / 100;
    }
    
    return priceNum.toFixed(2).replace('.', ',') + ' €';
  }

  getOptimizedImageUrl(url, size) {
    if (!url) return this.getPlaceholderImage();
    return url.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
  }

  getPlaceholderImage() {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23f5f5f5" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="Arial" font-size="12" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EKein Bild%3C/text%3E%3C/svg%3E';
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return String(text).replace(/[&<>"']/g, m => map[m]);
  }
}

// Initialisierung
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PredictiveSearch());
} else {
  new PredictiveSearch();
}
